import { Check } from 'lucide-react';

export interface StepperStep {
  label: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Zero-based index of the current step. */
  currentIndex: number;
  className?: string;
}

type StepState = 'done' | 'current' | 'future';

function stateOf(index: number, currentIndex: number): StepState {
  if (index < currentIndex) return 'done';
  if (index === currentIndex) return 'current';
  return 'future';
}

// Circle/connector shape (rounded-full, size, connectors) is intentionally
// unchanged from ui-guidelines.md §3 (Step Indicators) — this component is a
// numbered-circle wizard progress bar, structurally different from the
// guideline's pill/tab shape. Only the values that transfer without changing
// that shape are applied below: the active step's bold (700) label, and the
// inactive step's `rgba(0,25,40,0.1)` background (no border).
const CIRCLE_CLASSES: Record<StepState, string> = {
  done: 'bg-navy text-white',
  current: 'bg-green text-white',
  future: 'bg-navy/10 text-slate',
};

const LABEL_CLASSES: Record<StepState, string> = {
  done: 'font-semibold text-navy',
  current: 'font-bold text-navy',
  future: 'text-slate',
};

/**
 * Horizontal wizard progress indicator: numbered circles with labels beneath
 * and connectors between steps (solid once the segment is completed, dashed
 * ahead of the current step). Matches the create-startup Figma stepper.
 *
 * Below the `sm` breakpoint the circles shrink and only the current step keeps
 * its label: four 112px label columns cannot fit a phone, and were clipped
 * there (MYN-300 QA). From `sm` up it is unchanged.
 */
export function Stepper({ steps, currentIndex, className = '' }: StepperProps) {
  return (
    // Connectors flex so the stepper always spans exactly the form column
    // width (Figma: stepper and cards share the same 760px column).
    <ol className={`flex w-full items-start ${className}`.trim()}>
      {steps.map((step, index) => {
        const state = stateOf(index, currentIndex);
        return (
          <li key={step.label} className={`flex items-start ${index > 0 ? 'flex-1' : ''}`.trim()}>
            {index > 0 && (
              <span
                aria-hidden="true"
                className={`mx-2 mt-4 min-w-3 flex-1 sm:mx-4 sm:mt-5.25 sm:min-w-6 ${
                  index <= currentIndex
                    ? 'border-t-2 border-navy'
                    : 'border-t border-dashed border-slate/60'
                }`}
              />
            )}
            <span
              aria-current={state === 'current' ? 'step' : undefined}
              className="flex flex-col items-center gap-2 sm:w-28"
            >
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full font-sans text-sm font-semibold sm:h-11 sm:w-11 ${CIRCLE_CLASSES[state]}`}
              >
                {state === 'done' ? <Check aria-label="Completed" size={18} /> : index + 1}
              </span>
              <span
                className={`whitespace-nowrap text-center font-sans text-sm ${
                  state === 'current' ? '' : 'max-sm:sr-only'
                } ${LABEL_CLASSES[state]}`}
              >
                {step.label}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
